import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useMachineStore, useMapStore } from "@boundary/_hooks/useMapStore";
import { useServiceMetadata } from "@boundary/_hooks/useServiceMetadata";
import { ServiceMetadataStore } from "@infrastructure/stores/serviceMetadata.store";
import { MachineService } from "@domain/services/machine.service";
import type { MachineItem, MachineServiceItem } from "@domain/dtos/models/machine.models";
import { ImageGallery } from "@boundary/widgets/orderContent";
import { ServiceInfoPopup } from "@boundary/widgets/serviceInfoPopup";
import { GeoIcon } from "@boundary/components/mapIcons";
import { ResetIcon, BackIcon } from "@boundary/components/icons";
import { CopyableAddress } from "@boundary/components/copyableAddress";
import { Drawer } from "vaul";
import { Swiper, SwiperSlide } from "swiper/react";
import { pluralize } from "@shared/utils/locale/pluralize";
import { createPortal } from "react-dom";
import { prefetchAndNavigate } from "@boundary/utils/prefetchRoute";
import styles from "./machineDetailsWidget.module.scss";
import orderContentStyles from "./orderContent.module.scss";

const SNAP_POINTS = [0.44, 0.889] as const;

interface MachineDetailsWidgetProps {
  machineId: number | null;
  onClose: () => void;
  onCreateOrder?: (machineId: number) => void;
}

function useMachineData(machineId: number | null) {
  const [machine, setMachine] = useState<MachineItem | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!machineId) {
      setMachine(null);
      setLoading(false);
      return;
    }

    const loadMachine = async () => {
      setLoading(true);
      try {
        const data = await MachineService.getMachineById(machineId.toString());
        setMachine(data || null);
      } catch (error) {
        setMachine(null);
      } finally {
        setLoading(false);
      }
    };

    void loadMachine();
  }, [machineId]);

  return { machine, loading };
}

function useMapCentering(machineId: number | null) {
  const { map } = useMapStore();
  const { items } = useMachineStore();

  useEffect(() => {
    if (!machineId || !map) return;

    const item = items.find((item) => item.id === machineId);
    if (item && item.latitude !== null && item.longitude !== null) {
      setTimeout(() => {
        map.setLocation({
          center: [item.longitude, item.latitude],
          zoom: 16
        });
      }, 100);
    }
  }, [machineId, map, items]);
}

function MachineDetailsHeader({
  onClose,
  hasActiveFilters,
  onResetFilters,
  onLocate
}: {
  onClose: () => void;
  hasActiveFilters: boolean;
  onResetFilters: () => void;
  onLocate: () => void;
}) {
  return (
    <div className={styles.sheetHeader}>
      <button
        onClick={() => {
          console.log(`[Button Click] MachineDetailsWidget: "Закрыть детали аппарата"`);
          onClose();
        }}
        className={styles.headerButton}
      >
        <BackIcon size={14} />
      </button>

      {hasActiveFilters && (
        <button
          onClick={() => {
            console.log(`[Button Click] MachineDetailsWidget: "Сбросить фильтры"`);
            onResetFilters();
          }}
          className={styles.resetButton}
        >
          Сбросить фильтры
          <ResetIcon size={24} />
        </button>
      )}

      <button
        className={styles.locateButton}
        onClick={() => {
          console.log(`[Button Click] MachineDetailsWidget: "Определить местоположение"`);
          onLocate();
        }}
      >
        <GeoIcon size="small" />
      </button>
    </div>
  );
}

function MachineDetailsLoading() {
  return (
    <>
      <Drawer.Title className={styles.srOnly}>Загрузка информации об аппарате</Drawer.Title>
      <div className={styles.loadingContainer}>
        <div className={styles.loadingText}>Загрузка...</div>
      </div>
    </>
  );
}

function MachineDetailsContent({ machine }: { machine: MachineItem }) {
  const endTimeText = machine.working_hours || "";
  const openStatusText = machine.status === "online" ? "Открыто" : "Закрыто";

  return (
    <>
      <div className={orderContentStyles.infoDescription}>
        <CopyableAddress address={machine.address} />
        {machine.description && <p>{machine.description}</p>}
      </div>

      <ul className={orderContentStyles.infoProps}>
        <li className={orderContentStyles.infoPropsItem}>
          <div className={orderContentStyles.infoPropsItemTitle}>{endTimeText}</div>
          <div className={orderContentStyles.infoPropsItemValue}>{openStatusText}</div>
        </li>
        <li className={orderContentStyles.infoPropsItem}>
          <div className={orderContentStyles.infoPropsItemTitle}>Свободно</div>
          <div className={orderContentStyles.infoPropsItemValue}>
            {machine.free_cells}{" "}
            {pluralize(machine.free_cells, ["ячейка", "ячейки", "ячеек"])}
          </div>
        </li>
        <li className={orderContentStyles.infoPropsItem}>
          <div className={orderContentStyles.infoPropsItemTitle}>Станция</div>
          <div className={orderContentStyles.infoPropsItemValue}>{machine.machine_number}</div>
        </li>
      </ul>
    </>
  );
}

function MachineDetailsServices({
  services,
  machineId,
}: {
  services: MachineItem["services"];
  machineId: number;
}) {
  const navigate = useNavigate();
  const [selectedService, setSelectedService] = useState<MachineServiceItem | null>(null);
  useServiceMetadata();

  const handleServiceAction = useCallback(() => {
    if (!selectedService) return;
    const hasVariants = ServiceMetadataStore.getHasVariants(selectedService.service_type);
    if (hasVariants) {
      prefetchAndNavigate(
        navigate,
        `/service-variants?machineId=${machineId}&parent=${selectedService.service_type}`,
      );
    } else {
      prefetchAndNavigate(
        navigate,
        `/service?machineId=${machineId}&serviceType=${selectedService.service_type}`,
      );
    }
  }, [selectedService, machineId, navigate]);

  if (!services || services.length === 0) {
    return null;
  }

  return (
    <div className={`${orderContentStyles.infoServices} ${styles.servicesContainer}`}>
      <h2 className={orderContentStyles.infoServicesTitle}>Доступные услуги</h2>
      <Swiper
        className={styles.swiper}
        slidesPerView="auto"
        spaceBetween={0}
      >
        {services.map((service) => (
          <SwiperSlide className={styles.servicesSliderItem} key={service.id}>
            <button
              type="button"
              className={styles.servicesSliderItemWrap}
              onClick={() => setSelectedService(service)}
            >
              <div className={styles.servicesSliderItemPicture}>
                <img
                  className={styles.servicesSliderItemImg}
                  src={service.icon_url || "/assets/map/printerService.png"}
                  alt=""
                />
              </div>
              <div className={styles.servicesSliderItemTitle}>
                {service.name}
              </div>
            </button>
          </SwiperSlide>
        ))}
      </Swiper>

      {selectedService && (
        <ServiceInfoPopup
          open={!!selectedService}
          onOpenChange={(open) => { if (!open) setSelectedService(null); }}
          serviceType={selectedService.service_type}
          title={selectedService.name}
          description={selectedService.description || null}
          priceLabel={selectedService.price_label || null}
          iconUrl={selectedService.icon_url || null}
          hint={selectedService.hint || null}
          enabled={selectedService.enabled ?? true}
          onAction={handleServiceAction}
        />
      )}
    </div>
  );
}

function MachineDetailsTariffs({ tariffs }: { tariffs: MachineItem["tariffs"] }) {
  if (!tariffs || tariffs.length === 0) {
    return null;
  }

  // Сортируем тарифы по ID
  const sortedTariffs = [...tariffs].sort((a, b) => a.id - b.id);

  return (
    <div className={orderContentStyles.detailBottom}>
      <h2 className={orderContentStyles.detailBottomTitle}>Тарифы на хранение</h2>
      <ul className={orderContentStyles.detailBottomInfo}>
        {sortedTariffs.map((tariff) => (
          <li key={tariff.id}>{tariff.description}</li>
        ))}
      </ul>
    </div>
  );
}

function MachineDetailsButton({
  machine,
  onCreateOrder
}: {
  machine: MachineItem;
  onCreateOrder: (machineId: number) => void;
}) {
  // Определяем состояние кнопки на основе новых полей
  const getButtonState = () => {
    if (machine.status === 'offline') {
      return {
        disabled: true,
        text: 'Аппарат недоступен',
        className: styles.bottomControlBtnDisabled
      };
    }
    
    if (machine.status === 'closed') {
      return {
        disabled: true,
        text: 'Место закрыто',
        className: styles.bottomControlBtnDisabled
      };
    }
    
    if (machine.user_can_order === false) {
      return {
        disabled: true,
        text: 'Сделать заказ',
        className: styles.bottomControlBtnDisabled
      };
    }
    
    if (machine.free_cells === 0) {
      return {
        disabled: true,
        text: 'Нет свободных ячеек',
        className: styles.bottomControlBtnDisabled
      };
    }
    
    return {
      disabled: false,
      text: 'Сделать заказ',
      className: styles.bottomControlBtn
    };
  };

  const buttonState = getButtonState();

  const handleClick = () => {
    if (!buttonState.disabled) {
      onCreateOrder(machine.id);
    }
  };

  // Скрываем кнопку полностью, если лимит превышен (user_can_order === false)
  if (machine.user_can_order === false && machine.ban_message) {
    return (
      <>
        <div className={styles.spacer}></div>
        {createPortal(
          <div className={styles.bottomControl}>
            <div className={styles.banMessage}>
              {machine.ban_message}
            </div>
          </div>,
          document.body
        )}
      </>
    );
  }

  return (
    <>
      <div className={styles.spacer}></div>
      {createPortal(
        <div className={styles.bottomControl}>
          <button 
            className={buttonState.className}
            onClick={() => {
              console.log(`[Button Click] MachineDetailsButton: "${buttonState.text}" (machine_id: ${machine.id}, disabled: ${buttonState.disabled})`);
              handleClick();
            }}
            disabled={buttonState.disabled}
          >
            {buttonState.text}
          </button>
        </div>,
        document.body
      )}
    </>
  );
}

export function MachineDetailsWidget({
  machineId,
  onClose,
  onCreateOrder
}: MachineDetailsWidgetProps) {
  const { resetFilters, filteredTypes } = useMachineStore();
  const { locate } = useMapStore();
  const { machine, loading } = useMachineData(machineId);
  const [snap, setSnap] = useState<string | number | null>(SNAP_POINTS[0]);
  const [isOpen, setIsOpen] = useState(true);
  const scrollRef = useRef<HTMLDivElement>(null);
  const hasActiveFilters = filteredTypes.length > 0;

  useMapCentering(machineId);

  // Сбрасываем scroll на верх при закрытии drawer
  useEffect(() => {
    if (snap === SNAP_POINTS[0] && scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [snap]);

  useEffect(() => {
    if (machineId) {
      setIsOpen(true);
    }
  }, [machineId]);

  const handleResetFilters = useCallback(() => {
    resetFilters();
  }, [resetFilters]);

  const handleLocate = useCallback(() => {
    void locate();
  }, [locate]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setIsOpen(open);
      if (!open) {
        onClose();
      }
    },
    [onClose]
  );

  if (!machineId) {
    return null;
  }

  return (
    <Drawer.Root
      snapPoints={SNAP_POINTS as any}
      activeSnapPoint={snap}
      setActiveSnapPoint={setSnap}
      snapToSequentialPoint
      modal={false}
      dismissible={false}
      open={isOpen}
      onOpenChange={handleOpenChange}
    >
      <Drawer.Overlay className={styles.overlay} />
      <Drawer.Portal>
        <Drawer.Content
          aria-describedby={undefined}
          className={`${snap === 0.889 ? styles.sheetFull : ''} ${styles.sheet}`.trim()}
        >
          {/* Декоративная полоска сверху */}
          <div className={styles.dragHandleBar} />

          <div className={styles.sheetWrap}>
            <MachineDetailsHeader
              onClose={onClose}
              hasActiveFilters={hasActiveFilters}
              onResetFilters={handleResetFilters}
              onLocate={handleLocate}
            />

            <div ref={scrollRef} className={`${orderContentStyles.info} ${styles.infoSheet}`}>
              {loading || !machine ? (
                <MachineDetailsLoading />
              ) : (
                <>
                  <Drawer.Title className={orderContentStyles.infoName}>{machine.title}</Drawer.Title>
                  <MachineDetailsContent machine={machine} />
                  {machine.photos && machine.photos.length > 0 && (
                    <ImageGallery images={machine.photos.map((photo) => photo.url)} />
                  )}
                  <MachineDetailsServices services={machine.services} machineId={machineId} />
                  <MachineDetailsTariffs tariffs={machine.tariffs} />
                  {onCreateOrder && machineId && (
                    <MachineDetailsButton machine={machine} onCreateOrder={onCreateOrder} />
                  )}
                </>
              )}
            </div>
          </div>
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
}
